import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Library as LibraryIcon, BookOpen, Lock, Calendar } from "lucide-react";

import { useTotalUnreadCount } from "@/hooks/useTotalUnreadCount";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessLevel } from "@/components/SubscriptionGate";
import { isCommunityGated } from "@/services/accessGating";
import GuestPromptDialog from "./GuestPromptDialog";

const CustomHomeIcon = ({ className }: { className?: string }) => (
  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M10 16.75V20.25H6.25C5.14543 20.25 4.25 19.3546 4.25 18.25V9.95996C4.25 9.30046 4.57513 8.68335 5.11907 8.31042L11.3691 4.02539C12.0506 3.55811 12.9494 3.55811 13.6309 4.02539L19.8809 8.31042C20.4249 8.68335 20.75 9.30046 20.75 9.95996V18.25C20.75 19.3546 19.8546 20.25 18.75 20.25H15V16.75C15 15.3693 13.8807 14.25 12.5 14.25C11.1193 14.25 10 15.3693 10 16.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

const CustomMessageIcon = ({ className }: { className?: string }) => (
  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M12.5 21.25C17.6086 21.25 21.75 17.1086 21.75 12C21.75 6.89137 17.6086 2.75 12.5 2.75C7.39137 2.75 3.25 6.89137 3.25 12C3.25 13.3108 3.52267 14.558 4.01437 15.6878C4.17129 16.0484 4.21278 16.4524 4.09677 16.8281L3.32118 19.3402C2.9689 20.4812 4.02634 21.5564 5.17306 21.2231L7.82685 20.4518C8.18827 20.3468 8.57392 20.3864 8.92094 20.5321C10.0219 20.9945 11.2311 21.25 12.5 21.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CustomUserIcon = ({ className }: { className?: string }) => (
  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M6.35697 18.9157C7.67056 16.9968 9.83203 15.75 12.5 15.75C15.168 15.75 17.3294 16.9968 18.643 18.9157M6.35697 18.9157C7.99061 20.3679 10.1423 21.25 12.5 21.25C14.8577 21.25 17.0094 20.3679 18.643 18.9157M6.35697 18.9157C4.45086 17.2214 3.25 14.7509 3.25 12C3.25 6.89137 7.39137 2.75 12.5 2.75C17.6086 2.75 21.75 6.89137 21.75 12C21.75 14.7509 20.5491 17.2214 18.643 18.9157M15.75 10C15.75 11.7949 14.2949 13.25 12.5 13.25C10.7051 13.25 9.25 11.7949 9.25 10C9.25 8.20507 10.7051 6.75 12.5 6.75C14.2949 6.75 15.75 8.20507 15.75 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

const CustomCommunityIcon = ({ className }: { className?: string }) => (
  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M14.5 13.4304C17.3903 12.23 20.9269 13.5406 22.4061 17.3624C23.0342 18.9851 21.596 20.5 19.8561 20.5H16.25M11 7C11 8.79493 9.54489 10.25 7.74996 10.25C5.95504 10.25 4.49996 8.79493 4.49996 7C4.49996 5.20507 5.95504 3.75 7.74996 3.75C9.54489 3.75 11 5.20507 11 7ZM11.1382 20.5H4.71888C2.97897 20.5 1.5413 18.9823 2.16984 17.3598C4.4219 11.5467 11.4352 11.5467 13.6872 17.3598C14.3158 18.9823 12.8781 20.5 11.1382 20.5ZM20.25 7C20.25 8.79493 18.7949 10.25 17 10.25C15.205 10.25 13.75 8.79493 13.75 7C13.75 5.20507 15.205 3.75 17 3.75C18.7949 3.75 20.25 5.20507 20.25 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CustomCalendarIcon = ({ className }: { className?: string }) => (
  <Calendar className={className} />
);

interface BottomNavigationProps {
  currentPath: string;
}

const BottomNavigation = ({ currentPath }: BottomNavigationProps) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isGuest = !isAuthenticated;
  const [guestPromptOpen, setGuestPromptOpen] = useState(false);

  const { totalUnreadCount } = useTotalUnreadCount();
  const { accessLevel } = useAccessLevel();

  const communityGated = isCommunityGated(accessLevel);

  const handleNavClick = (page: string) => {
    const authRequiredPages = ['home', 'messages', 'profile', 'community'];
    
    if (isGuest && authRequiredPages.includes(page)) {
      setGuestPromptOpen(true);
      return;
    }

    // Course-only users get redirected to subscribe screen for gated tabs.
    //
    // 'home' is deliberately NOT gated. The Home ROUTE has never been protected
    // (ProtectedRoute is auth-only), so a course-only user reaching '/' by any
    // other means — deep link, typed URL, post-login landing — already sees it,
    // and it renders a correct non-member state: "עוד לא הצטרפת לפעילות" with a
    // CTA into the library. Gating only the TAB therefore protected nothing; it
    // just ejected people out of the app's own home screen into a full-screen
    // paywall, which is where the 1-second bounce loops came from. '/feed' and
    // '/chat' ARE route-guarded, so they stay here.
    const gatedPages = ['messages', 'community'];
    if (communityGated && gatedPages.includes(page)) {
      navigate('/subscribe');
      return;
    }

    switch (page) {
      case 'library':
        navigate('/library', { state: { tab: 'courses' } });
        break;
      case 'events':
        navigate('/events');
        break;
      case 'community':
        navigate('/feed');
        break;
      case 'home':
        navigate('/');
        break;
      case 'messages':
        navigate('/chat');
        break;
      case 'profile':
        navigate('/profile');
        break;
    }
  };

  function BottomNavItem({ 
    icon: Icon, 
    label, 
    active, 
    onClick,
    disabled = false,
    locked = false,
    badge = 0
  }: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    active?: boolean;
    onClick?: () => void;
    disabled?: boolean;
    locked?: boolean;
    badge?: number;
  }) {
    const isActiveEnabled = active && !disabled;
    return (
      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={`flex flex-1 flex-col items-center justify-center gap-0.5 pt-2 pb-0 transition-colors duration-200 relative ${
          disabled
            ? "text-muted-foreground/40 cursor-not-allowed"
            : active
            ? "text-primary"
            : "text-muted-foreground"
        }`}
      >
        <div className={`relative ${isActiveEnabled ? "[&_path]:[stroke-width:2.25]" : ""}`}>
          <Icon className="h-5 w-5" />
          {locked && (
            <div className="absolute -top-1 -right-1 bg-background rounded-full p-0.5">
              <Lock className="h-3 w-3 text-muted-foreground" />
            </div>
          )}
          {badge > 0 && !locked && (
            <div className="absolute -top-2 -left-2 bg-primary text-primary-foreground text-xs font-medium rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1">
              {badge > 99 ? '99+' : badge}
            </div>
          )}
        </div>
        <span className={`text-sm leading-tight ${isActiveEnabled ? "font-semibold" : "font-medium"}`}>{label}</span>
      </button>
    );
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return currentPath === '/';
    }
    return currentPath === path;
  };

  // Guest bottom nav: only Library and Events
  if (isGuest) {
    return (
      <>
        <nav className="fixed inset-x-0 bottom-0 z-50 border-t bg-card pb-[max(env(safe-area-inset-bottom),12px)] backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <div className="mx-auto flex w-full px-4 md:max-w-2xl md:px-6 lg:max-w-4xl lg:px-8 xl:max-w-6xl">
            <BottomNavItem 
              icon={isActive('/library') ? BookOpen : LibraryIcon} 
              label="ספרייה" 
              active={isActive('/library')}
              onClick={() => handleNavClick('library')} 
            />
            <BottomNavItem 
              icon={CustomCalendarIcon as any} 
              label="יומן" 
              active={isActive('/events')}
              onClick={() => handleNavClick('events')} 
            />
          </div>
        </nav>
        <GuestPromptDialog open={guestPromptOpen} onOpenChange={setGuestPromptOpen} />
      </>
    );
  }

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t bg-card pb-[max(env(safe-area-inset-bottom),12px)] backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="mx-auto flex w-full px-4 md:max-w-2xl md:px-6 lg:max-w-4xl lg:px-8 xl:max-w-6xl">
        <BottomNavItem 
          icon={isActive('/library') ? BookOpen : LibraryIcon} 
          label="ספרייה" 
          active={isActive('/library')}
          onClick={() => handleNavClick('library')} 
        />
        <BottomNavItem 
          icon={CustomCommunityIcon as any} 
          label="קהילה" 
          active={isActive('/feed')}
          onClick={() => handleNavClick('community')}
          locked={communityGated}
        />
        <BottomNavItem 
          icon={CustomHomeIcon as any} 
          label="בית"
          active={isActive('/')}
          onClick={() => handleNavClick('home')}
        />
        <BottomNavItem 
          icon={CustomMessageIcon as any} 
          label="הודעות" 
          active={isActive('/chat')}
          onClick={() => handleNavClick('messages')}
          badge={communityGated ? 0 : totalUnreadCount}
          locked={communityGated}
        />
        <BottomNavItem 
          icon={CustomUserIcon as any} 
          label="פרופיל" 
          active={isActive('/profile')}
          onClick={() => handleNavClick('profile')} 
        />
      </div>
    </nav>
  );
};

export default BottomNavigation;